import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, StatusBadge, EmptyState } from "@/components/common";
import CompanySelect from "@/components/CompanySelect";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, errMsg, fmtDate } from "@/lib/api";

export const COMMERCIAL = {
  trial: ["Trial", "bg-primary/15 text-black border-primary"], active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  past_due: ["Past due", "bg-accent/40 text-black border-accent"], grace_period: ["Grace period", "bg-amber-100 text-amber-900 border-amber-300"],
  suspended: ["Suspended", "bg-red-50 text-red-700 border-red-300"], cancelled: ["Cancelled", "bg-gray-100 text-gray-600 border-gray-200"],
  expired: ["Expired", "bg-gray-100 text-gray-600 border-gray-200"], no_subscription: ["No subscription", "bg-gray-50 text-gray-600 border-gray-200"],
};
export const CommercialBadge = ({ c, testId }) => {
  const [l, cls] = COMMERCIAL[c.status] || [c.status, ""];
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={testId}>{l}{c.status === "trial" ? ` · ${c.days_left} days left` : ""}</span>;
};

function AccountRow({ row, reload }) {
  const [f, setF] = useState({ service_model: row.account.service_model, scope: (row.account.managed_scope || []).join("\n"), notes: row.account.notes || "" });
  const save = () => api.put(`/billing/accounts/${row.tenant_id}`, { service_model: f.service_model, managed_scope: f.scope.split("\n"), notes: f.notes }).then(() => { toast.success("Saved"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const trial = () => api.post(`/billing/accounts/${row.tenant_id}/trial`).then(() => { toast.success("Trial started"); reload(); }).catch((e) => toast.error(errMsg(e)));
  const t = row.account.trial;
  return (
    <div className="space-y-3 rounded-xl border bg-card p-4" data-testid={`account-row-${row.tenant_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-heading font-bold">{row.company_name}</div>
        <CommercialBadge c={row.commercial} testId={`account-status-${row.tenant_id}`} />
      </div>
      <div className="text-xs text-muted-foreground">{t ? `Trial ${fmtDate(t.start_date)} → ${fmtDate(t.end_date)} (${t.days} days) · ${t.status}${t.converted_at ? " · converted" : ""}` : "No trial recorded"}</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Service model"><Select value={f.service_model} onValueChange={(v) => setF({ ...f, service_model: v })}><SelectTrigger data-testid={`account-model-${row.tenant_id}`}><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="software_only">Software Only</SelectItem><SelectItem value="managed">Managed Compliance</SelectItem></SelectContent></Select></Field>
        {f.service_model === "managed" && <Field label="Managed scope (one obligation per line)"><Textarea rows={3} value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} data-testid={`account-scope-${row.tenant_id}`} /></Field>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={save} data-testid={`account-save-${row.tenant_id}`}>Save</Button>
        {!t && row.commercial.status !== "active" && <Button size="sm" variant="outline" onClick={trial} data-testid={`account-trial-${row.tenant_id}`}>Start trial</Button>}
      </div>
    </div>
  );
}

export function Accounts() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/billing/accounts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-3" data-testid="accounts-panel">
      <p className="text-sm text-muted-foreground">Commercial status is separate from compliance status. Late payment never deletes or pauses records. Restrictions only apply if you switch them on in Settings.</p>
      {rows.map((r) => <AccountRow key={r.tenant_id} row={r} reload={load} />)}
    </div>
  );
}

const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); };

export function MonthlySummaries() {
  const { isStaff, user } = useAuth();
  const [tid, setTid] = useState(isStaff ? "" : user.tenant_id);
  const [month, setMonth] = useState(lastMonth());
  const [shared, setShared] = useState([]);
  useEffect(() => { if (tid) { api.get("/billing/shared-summaries", { params: { tenant_id: tid } }).then((r) => setShared(r.data)); } }, [tid]);
  const share = () => api.post("/billing/monthly-summary/share", { tenant_id: tid, month }).then(() => { toast.success("Shared with the company's owners"); setShared((s) => [{ month, shared_at: new Date().toISOString() }, ...s.filter((x) => x.month !== month)]); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4" data-testid="summaries-panel">
      {isStaff && <div className="grid max-w-2xl gap-3 sm:grid-cols-[1fr_160px]">
        <Field label="Company"><CompanySelect value={tid} onChange={setTid} testId="summary-company" /></Field>
        <Field label="Month"><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="summary-month" /></Field>
      </div>}
      {isStaff && tid && <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => window.open(`/billing/summary/${tid}/${month}`, "_blank")} data-testid="summary-open-btn">Open / print summary</Button>
        {can(user, "billing_share") && <Button onClick={share} data-testid="summary-share-btn">Share with client owners</Button>}
      </div>}
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase text-muted-foreground">Shared summaries</p>
        {!shared.length ? <EmptyState title="No summaries shared yet" testId="summaries-empty" /> : shared.map((s) => (
          <Link key={s.month} to={`/billing/summary/${tid}/${s.month}`} target="_blank" className="flex items-center justify-between rounded-lg border bg-card p-3 text-sm hover:border-primary" data-testid={`summary-link-${s.month}`}>
            <span className="font-semibold">Billing summary · {s.month}</span><span className="text-xs text-muted-foreground">shared {fmtDate(s.shared_at)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function CommercialBanner() {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => { if (user.role.startsWith("client") && user.perms?.billing_view) { api.get("/billing/account").then((r) => setC(r.data.commercial)).catch(() => {}); } }, [user.role, user.tenant_id, user.perms?.billing_view]);
  if (!c || !["trial", "past_due", "grace_period", "suspended", "expired"].includes(c.status)) return null;
  const msg = { trial: `Trial: ${c.days_left} day(s) left.`, past_due: "An invoice is past due.", grace_period: "Your account is in its grace period: an invoice is overdue.",
    suspended: c.restrictions_on ? "Account suspended: new paid work is paused. Your records, downloads and billing stay available." : "Account overdue: please settle outstanding invoices.",
    expired: "Your trial has ended. Your records are safe." }[c.status];
  return (
    <div className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm ${c.status === "trial" ? "border-primary bg-primary/10" : "border-accent bg-accent/25"}`} data-testid="commercial-banner">
      <span>{msg}</span><Link to="/billing" className="font-semibold underline" data-testid="commercial-banner-link">View billing</Link>
    </div>
  );
}
